import React, { useState, useEffect, useRef, Suspense, memo } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

// Lazy load heavy components
const Hero = React.lazy(() => import('./components/Hero'));
const Services = React.lazy(() => import('./components/Services'));
const Features = React.lazy(() => import('./components/Features'));
const Testimonials = React.lazy(() => import('./components/Testimonials'));
const Contact = React.lazy(() => import('./components/Contact'));
const Cotizador = React.lazy(() => import('./components/Cotizador'));

// Lazy load auth components
const Login = React.lazy(() => import('./auth/Login'));
const Register = React.lazy(() => import('./auth/Register'));
const ForgotPassword = React.lazy(() => import('./auth/ForgotPassword'));
const VerifyEmail = React.lazy(() => import('./auth/VerifyEmail'));

// Lazy load dashboard components
const Dashboard = React.lazy(() => import('./dashboard/Dashboard'));
const CotizadorPage = React.lazy(() => import('./dashboard/CotizadorPage'));
const PaymentsPage = React.lazy(() => import('./dashboard/PaymentsPage'));
const ShippingPage = React.lazy(() => import('./dashboard/ShippingPage'));
const ProfilePage = React.lazy(() => import('./dashboard/ProfilePage'));
const NotificationsPage = React.lazy(() => import('./dashboard/NotificationsPage'));
const AnalyticsPage = React.lazy(() => import('./dashboard/AnalyticsPage'));
const EcommercePage = React.lazy(() => import('./dashboard/EcommercePage'));
const AdminPage = React.lazy(() => import('./dashboard/AdminPage'));
const SecurityPage = React.lazy(() => import('./dashboard/SecurityPage'));

// Keep critical components loaded synchronously
import ProtectedRoute from './auth/ProtectedRoute';
import { AuthProvider } from './contexts/AuthContext';
import { PaymentProvider } from './contexts/PaymentContext';
import { ShippingProvider } from './contexts/ShippingContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { EcommerceProvider } from './contexts/EcommerceContext';
import { AdminProvider } from './contexts/AdminContext';
import { SecurityProvider } from './contexts/SecurityContext';
import NotificationContainer from './components/NotificationContainer';
import ModeSwitcher from './components/ModeSwitcher';
import logoImage from './images/logo.png';
import { Link } from 'react-router-dom';

const AppContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
`;

const Header = styled(motion.header)`
  background-color: ${({ scrolled }) => (scrolled ? 'rgba(0, 0, 0, 0.98)' : 'rgba(0, 0, 0, 0.92)')};
  padding: ${({ scrolled }) => (scrolled ? '0.8rem 2rem' : '1.1rem 2rem')};
  position: fixed;
  width: 100%;
  top: 0;
  z-index: 1000;
  border-bottom: 1px solid ${({ scrolled }) => (scrolled ? '#D4AF37' : 'transparent')};
  backdrop-filter: blur(15px);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${({ scrolled }) => (scrolled ? '0 8px 25px rgba(0, 0, 0, 0.4)' : 'none')};
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: ${({ scrolled }) => (scrolled ? '0.8rem 1.2rem' : '1rem 1.2rem')};
  }
  
  @media (max-width: 480px) {
    padding: ${({ scrolled }) => (scrolled ? '0.7rem 1rem' : '0.9rem 1rem')};
    min-height: 70px;
  }
`;

const Nav = styled.nav`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const LogoContainer = styled(motion.div)`
  display: flex;
  align-items: center;
  flex-shrink: 1;
  max-width: 80%;
  
  @media (max-width: 480px) {
    max-width: 75%;
  }
`;

const LogoImg = styled.img`
  height: ${({ scrolled }) => (scrolled ? '50px' : '60px')};
  width: auto;
  transition: all 0.3s ease;
  object-fit: contain;
  
  @media (max-width: 768px) {
    height: ${({ scrolled }) => (scrolled ? '45px' : '50px')};
  }
  
  @media (max-width: 480px) {
    height: ${({ scrolled }) => (scrolled ? '40px' : '45px')};
  }
`;

const LogoText = styled.span`
  color: #D4AF37;
  font-size: ${({ scrolled }) => (scrolled ? '1.5rem' : '1.7rem')};
  font-weight: 700;
  margin-left: 15px;
  text-shadow: 0px 0px 8px rgba(212, 175, 55, 0.3);
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    font-size: ${({ scrolled }) => (scrolled ? '1.3rem' : '1.5rem')};
    margin-left: 12px;
  }
  
  @media (max-width: 480px) {
    font-size: ${({ scrolled }) => (scrolled ? '1.1rem' : '1.3rem')};
    margin-left: 8px;
  }
`;

const MobileNavButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: #D4AF37;
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 1000;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2rem;
  align-items: center;

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileNavLinks = styled(motion.div)`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 80px;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.98);
    padding: 2.5rem;
    gap: 1.8rem;
    border-top: 1px solid rgba(212, 175, 55, 0.1);
    transform: ${({ isOpen }) => isOpen ? 'translateY(0)' : 'translateY(-100%)'};
    opacity: ${({ isOpen }) => isOpen ? '1' : '0'};
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    pointer-events: ${({ isOpen }) => isOpen ? 'all' : 'none'};
    z-index: 999;
    width: 95%;
    max-width: 400px;
    max-height: 85vh;
    overflow-y: auto;
    margin: 0 auto;
    border: 1px solid rgba(212, 175, 55, 0.5);
    border-radius: 16px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.8);
    backdrop-filter: blur(20px);
    
    /* Hide scrollbar but keep functionality */
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
      display: none;
    }
    
    @media (max-width: 480px) {
      padding: 2rem 1.5rem;
      gap: 1.5rem;
      width: 92%;
      top: 70px;
      max-height: 80vh;
      border-radius: 12px;
    }
  }
`;

const NavLink = styled(Link)`
  color: #D4AF37;
  text-decoration: none;
  font-size: 1.1rem;
  font-weight: 500;
  transition: color 0.3s ease;
  position: relative;

  &:hover {
    color: #ffffff;
    
    &::after {
      width: 100%;
    }
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 0;
    width: 0;
    height: 2px;
    background: #D4AF37;
    transition: width 0.3s ease;
  }

  @media (max-width: 768px) {
    font-size: 1.2rem;
    text-align: center;
  }
`;

const MobileNavOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(5px);
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MobileMenuButton = styled(motion.button)`
  display: none;
  background: rgba(212, 175, 55, 0.15);
  border: 1px solid #D4AF37;
  color: #D4AF37;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.4rem;
  border-radius: 8px;
  z-index: 1001;
  transition: all 0.3s ease;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 5px;
  position: relative;
  right: 0;

  &:hover {
    background: rgba(212, 175, 55, 0.25);
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    display: flex;
  }
  
  @media (max-width: 480px) {
    width: 38px;
    height: 38px;
    font-size: 1.2rem;
    padding: 0.3rem;
    margin-right: 2px;
  }
`;

const Footer = styled.footer`
  background-color: #000;
  color: #fff;
  padding: 3rem 2rem;
  text-align: center;
  border-top: 1px solid #D4AF37;

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const FooterLogo = styled.img`
  height: 60px;
  margin-bottom: 1.5rem;
`;

const FooterText = styled.p`
  margin: 0.5rem 0;
  color: #aaa;
  font-size: 0.9rem;
`;

const FooterCopyright = styled.p`
  margin-top: 2rem;
  color: #777;
  font-size: 0.85rem;
`;

// Loading component for Suspense fallback
const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
  background: transparent;
`;

const Spinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid rgba(212, 175, 55, 0.1);
  border-left: 4px solid #D4AF37;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingComponent = memo(() => (
  <LoadingSpinner>
    <Spinner />
  </LoadingSpinner>
));

const WhatsAppButton = styled(motion.a)`
  position: fixed;
  bottom: 25px;
  right: 25px;
  width: 68px;
  height: 68px;
  background: #25d366;
  color: white;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 6px 15px rgba(37, 211, 102, 0.5);
  z-index: 1000;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.3s ease;
  border: 3px solid rgba(255, 255, 255, 0.9);
  padding: 12px;
  
  &:hover {
    transform: scale(1.1) translateY(-5px);
    box-shadow: 0 8px 20px rgba(37, 211, 102, 0.8);
    background: #22c15e;
  }
  
  img, svg {
    width: 100%;
    height: 100%;
    object-fit: contain;
    filter: drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.2));
  }
  
  @media (max-width: 768px) {
    width: 62px;
    height: 62px;
    bottom: 20px;
    right: 20px;
    padding: 10px;
    border-width: 2.5px;
  }
  
  @media (max-width: 480px) {
    width: 56px;
    height: 56px;
    bottom: 15px;
    right: 15px;
    padding: 9px;
    border-width: 2px;
  }
`;

const WhatsAppBadge = styled.div`
  position: absolute;
  top: -8px;
  right: -8px;
  background: linear-gradient(to bottom, #ff5252, #e62222);
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 0.8rem;
  font-weight: bold;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.3);
  border: 2px solid white;
  
  @media (max-width: 768px) {
    width: 22px;
    height: 22px;
    font-size: 0.7rem;
    top: -6px;
    right: -6px;
    border-width: 1.5px;
  }
`;

const ContactText = styled(motion.span)`
  position: absolute;
  right: 75px;
  background: #25d366;
  color: white;
  padding: 8px 15px;
  border-radius: 20px;
  font-size: 0.9rem;
  white-space: nowrap;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
  font-weight: 500;
  border: 1px solid rgba(255, 255, 255, 0.3);
  
  &::after {
    content: '';
    position: absolute;
    right: -8px;
    top: 50%;
    transform: translateY(-50%);
    border-width: 8px 0 8px 10px;
    border-style: solid;
    border-color: transparent transparent transparent #25d366;
  }
  
  @media (max-width: 992px) {
    display: none;
  }
`;

const PulseAnimation = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(37, 211, 102, 0.8);
  }
  70% {
    transform: scale(1.05);
    box-shadow: 0 0 0 15px rgba(37, 211, 102, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(37, 211, 102, 0);
  }
`;

const PulseCircle = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  animation: ${PulseAnimation} 2s infinite;
  z-index: -1;
`;

function WhatsAppLogo() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" fill="#ffffff" width="100%" height="100%">
      <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"/>
    </svg>
  );
}

const App = memo(() => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showContactText, setShowContactText] = useState(false);
  
  const homeRef = useRef(null);
  const servicesRef = useRef(null);
  const featuresRef = useRef(null);
  const cotizadorRef = useRef(null);
  const testimonialsRef = useRef(null);
  const contactRef = useRef(null);
  
  const scrollToSection = (sectionId) => {
    console.log('Intentando navegar a:', sectionId);
    
    // Cerrar menú móvil si está abierto
    if (isMenuOpen) {
      setIsMenuOpen(false);
    }
    
    // Usar getElementById como método principal
    const element = document.getElementById(sectionId);
    if (element) {
      console.log('Elemento encontrado por ID:', element);
      element.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start',
        inline: 'nearest'
      });
    } else {
      console.log('No se encontró el elemento con ID:', sectionId);
      
      // Intentar con los refs como respaldo
      const sectionRefs = {
        'home': homeRef,
        'services': servicesRef,
        'features': featuresRef,
        'cotizador': cotizadorRef,
        'testimonials': testimonialsRef,
        'contact': contactRef
      };
      
      const ref = sectionRefs[sectionId];
      if (ref && ref.current) {
        console.log('Usando ref como respaldo para:', sectionId);
        ref.current.scrollIntoView({ behavior: 'smooth' });
      } else {
        console.log('No se encontró ni elemento ni ref para:', sectionId);
        // Fallback: scroll manual a la posición estimada
        const sectionPositions = {
          'home': 0,
          'services': 1000,
          'features': 2000,
          'cotizador': 3000,
          'testimonials': 4000,
          'contact': 5000
        };
        const position = sectionPositions[sectionId];
        if (position !== undefined) {
          console.log('Usando scroll manual a posición:', position);
          window.scrollTo({ top: position, behavior: 'smooth' });
        }
      }
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      if (offset > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const menuVariants = {
    closed: { opacity: 0, y: -20, scale: 0.95 },
    open: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: { 
        type: "spring",
        stiffness: 300,
        damping: 20,
        staggerChildren: 0.1, 
        delayChildren: 0.2 
      } 
    }
  };

  const linkVariants = {
    closed: { opacity: 0, y: -10 },
    open: { 
      opacity: 1, 
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20
      }
    }
  };

  const navItems = [
    { name: 'Inicio', href: '#home', onClick: () => scrollToSection('home') },
    { name: 'Servicios', href: '#services', onClick: () => scrollToSection('services') },
    { name: 'Características', href: '#features', onClick: () => scrollToSection('features') },
    { name: 'Cotizador', href: '#cotizador', onClick: () => scrollToSection('cotizador') },
    { name: 'Testimonios', href: '#testimonials', onClick: () => scrollToSection('testimonials') },
    { name: 'Contacto', href: '#contact', onClick: () => scrollToSection('contact') }
  ];

  return (
      <Router>
        <AuthProvider>
          <PaymentProvider>
            <ShippingProvider>
              <EcommerceProvider>
                <AdminProvider>
                  <SecurityProvider>
                    <NotificationProvider>
      <AppContainer>
        <Header 
          scrolled={scrolled}
          initial={{ y: -100 }}
          animate={{ y: 0 }}
          transition={{ 
            type: "spring",
            stiffness: 300,
            damping: 20
          }}
        >
          <Nav>
            <LogoContainer
              initial={{ opacity: 0, x: -30, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ 
                duration: 0.6, 
                ease: "easeOut",
                type: "spring",
                stiffness: 200,
                damping: 20
              }}
              whileHover={{ 
                scale: 1.02,
                transition: { duration: 0.2 }
              }}
            >
              <LogoImg src={logoImage} alt="Silva Transport Logo" scrolled={scrolled} />
              <LogoText scrolled={scrolled}>Silva Transport</LogoText>
            </LogoContainer>
            
            <NavLinks>
              {navItems.map((item, idx) => (
                <NavLink 
                  key={idx} 
                  to={item.href}
                  onClick={(e) => {
                    e.preventDefault();
                    item.onClick();
                  }}
                  initial={{ opacity: 0, y: -15, x: -10 }}
                  animate={{ opacity: 1, y: 0, x: 0 }}
                  transition={{ 
                    type: "spring",
                    stiffness: 300,
                    damping: 25,
                    delay: 0.4 + idx * 0.08 
                  }}
                  whileHover={{ 
                    scale: 1.03, 
                    y: -2,
                    transition: { duration: 0.2 }
                  }}
                  whileTap={{ 
                    scale: 0.97,
                    transition: { duration: 0.1 }
                  }}
                >
                  {item.name}
                </NavLink>
              ))}
              <NavLink 
                to="/auth/login"
                style={{ 
                  background: 'linear-gradient(135deg, #D4AF37, #F4D03F)',
                  color: '#000',
                  padding: '0.6rem 1.2rem',
                  borderRadius: '10px',
                  fontWeight: '700',
                  fontSize: '0.95rem',
                  letterSpacing: '0.3px',
                  boxShadow: '0 3px 10px rgba(212, 175, 55, 0.3)',
                  border: '2px solid transparent',
                  backgroundClip: 'padding-box'
                }}
                whileHover={{ 
                  scale: 1.05, 
                  y: -2,
                  boxShadow: '0 5px 15px rgba(212, 175, 55, 0.4)',
                  transition: { duration: 0.2 }
                }}
                whileTap={{ 
                  scale: 0.95,
                  y: 0,
                  transition: { duration: 0.1 }
                }}
              >
                Iniciar Sesión
              </NavLink>
            </NavLinks>
            
            <MobileMenuButton 
              onClick={toggleMenu} 
              animate={{ 
                rotate: isMenuOpen ? 180 : 0,
                scale: isMenuOpen ? 1.1 : 1
              }}
              transition={{ 
                duration: 0.4,
                ease: "easeInOut",
                type: "spring",
                stiffness: 300,
                damping: 20
              }}
              whileHover={{ 
                scale: 1.05,
                transition: { duration: 0.2 }
              }}
              whileTap={{ 
                scale: 0.95,
                transition: { duration: 0.1 }
              }}
              aria-label={isMenuOpen ? "Cerrar menú" : "Abrir menú"}
            >
              {isMenuOpen ? "×" : "☰"}
            </MobileMenuButton>
          </Nav>
        </Header>

        <AnimatePresence mode="wait">
          {isMenuOpen && (
            <MobileNavOverlay 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
              onClick={closeMenu}
            >
              <MobileNavLinks
                initial={{ opacity: 0, scale: 0.8, y: -20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, y: -20 }}
                transition={{ 
                  duration: 0.5, 
                  ease: "easeOut",
                  type: "spring",
                  stiffness: 300,
                  damping: 30
                }}
                onClick={(e) => e.stopPropagation()}
                isOpen={isMenuOpen}
              >
                <LogoImg 
                  src={logoImage} 
                  alt="Silva Transport Logo" 
                  style={{ height: '60px', marginBottom: '1rem' }} 
                />
                {navItems.map((item, idx) => (
                  <NavLink 
                    key={idx} 
                    to={item.href}
                    onClick={(e) => {
                      e.preventDefault();
                      item.onClick();
                    }}
                    initial={{ opacity: 0, y: -15, x: -10 }}
                    animate={{ 
                      opacity: 1, 
                      y: 0, 
                      x: 0,
                      transition: { 
                        delay: 0.3 + idx * 0.08,
                        duration: 0.4,
                        ease: "easeOut"
                      } 
                    }}
                    exit={{ 
                      opacity: 0, 
                      y: -15, 
                      x: -10,
                      transition: { 
                        duration: 0.2,
                        ease: "easeIn"
                      } 
                    }}
                    whileHover={{ 
                      scale: 1.02, 
                      x: 5,
                      transition: { duration: 0.2 }
                    }}
                    whileTap={{ 
                      scale: 0.98,
                      transition: { duration: 0.1 }
                    }}
                  >
                    {item.name}
                  </NavLink>
                ))}
                <NavLink 
                  to="/auth/login"
                  style={{ 
                    background: 'linear-gradient(135deg, #D4AF37, #F4D03F)',
                    color: '#000',
                    padding: '1.2rem 2rem',
                    borderRadius: '18px',
                    fontWeight: '700',
                    marginTop: '2.2rem',
                    textAlign: 'center',
                    display: 'block',
                    width: '100%',
                    fontSize: '1.18rem',
                    letterSpacing: '0.7px',
                    boxShadow: '0 8px 28px rgba(212, 175, 55, 0.25)',
                    border: '2px solid #D4AF37',
                    backgroundClip: 'padding-box',
                    transition: 'all 0.25s cubic-bezier(0.4,0,0.2,1)',
                  }}
                  initial={{ opacity: 0, y: -20, scale: 0.9 }}
                  animate={{ 
                    opacity: 1, 
                    y: 0, 
                    scale: 1,
                    transition: { 
                      delay: 0.4 + navItems.length * 0.08,
                      duration: 0.5,
                      ease: "easeOut"
                    } 
                  }}
                  exit={{ 
                    opacity: 0, 
                    y: -20, 
                    scale: 0.9,
                    transition: { 
                      duration: 0.3,
                      ease: "easeIn"
                    } 
                  }}
                  whileHover={{ 
                    scale: 1.05, 
                    y: -3,
                    boxShadow: '0 12px 32px rgba(212, 175, 55, 0.35)',
                    transition: { duration: 0.18 }
                  }}
                  whileTap={{ 
                    scale: 0.97,
                    y: 0,
                    transition: { duration: 0.1 }
                  }}
                >
                  Iniciar Sesión
                </NavLink>
              </MobileNavLinks>
            </MobileNavOverlay>
          )}
        </AnimatePresence>

        <Suspense fallback={<LoadingComponent />}>
          <Routes>
            <Route path="/" element={
              <>
                <div id="home" ref={homeRef}>
                  <Suspense fallback={<LoadingComponent />}>
                    <Hero />
                  </Suspense>
                </div>
                <div id="services" ref={servicesRef}>
                  <Suspense fallback={<LoadingComponent />}>
                    <Services />
                  </Suspense>
                </div>
                <div id="features" ref={featuresRef}>
                  <Suspense fallback={<LoadingComponent />}>
                    <Features />
                  </Suspense>
                </div>
                <div id="cotizador" ref={cotizadorRef}>
                  <Suspense fallback={<LoadingComponent />}>
                    <Cotizador />
                  </Suspense>
                </div>
                <div id="testimonials" ref={testimonialsRef}>
                  <Suspense fallback={<LoadingComponent />}>
                    <Testimonials />
                  </Suspense>
                </div>
                <div id="contact" ref={contactRef}>
                  <Suspense fallback={<LoadingComponent />}>
                    <Contact />
                  </Suspense>
                </div>
              </>
            } />
            <Route path="/auth/login" element={
              <Suspense fallback={<LoadingComponent />}>
                <Login />
              </Suspense>
            } />
            <Route path="/auth/register" element={
              <Suspense fallback={<LoadingComponent />}>
                <Register />
              </Suspense>
            } />
            <Route path="/auth/forgot-password" element={
              <Suspense fallback={<LoadingComponent />}>
                <ForgotPassword />
              </Suspense>
            } />
            <Route path="/auth/verify-email/:token" element={
              <Suspense fallback={<LoadingComponent />}>
                <VerifyEmail />
              </Suspense>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingComponent />}>
                  <Dashboard />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/cotizador" element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingComponent />}>
                  <CotizadorPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/payments" element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingComponent />}>
                  <PaymentsPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/shipping" element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingComponent />}>
                  <ShippingPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingComponent />}>
                  <ProfilePage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/notifications" element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingComponent />}>
                  <NotificationsPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/analytics" element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingComponent />}>
                  <AnalyticsPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/ecommerce" element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingComponent />}>
                  <EcommercePage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin" element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingComponent />}>
                  <AdminPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/security" element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingComponent />}>
                  <SecurityPage />
                </Suspense>
              </ProtectedRoute>
            } />
          </Routes>
        </Suspense>

        <Footer>
          <FooterContent>
            <FooterLogo src={logoImage} alt="Silva Transport Logo" />
          </FooterContent>
        </Footer>
        
        <WhatsAppButton 
          href="https://wa.me/5217298091703" 
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Contáctanos por WhatsApp"
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ 
            delay: 2.5,
            duration: 0.6,
            type: "spring",
            stiffness: 200,
            damping: 20
          }}
          whileHover={{ 
            scale: 1.1,
            rotate: 5,
            transition: { duration: 0.2 }
          }}
          whileTap={{ 
            scale: 0.9,
            transition: { duration: 0.1 }
          }}
          onMouseEnter={() => setShowContactText(true)}
          onMouseLeave={() => setShowContactText(false)}
        >
          <PulseCircle />
          <WhatsAppLogo />
          <WhatsAppBadge>1</WhatsAppBadge>
          {showContactText && (
            <ContactText
              initial={{ opacity: 0, x: 30, scale: 0.8 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 30, scale: 0.8 }}
              transition={{ 
                duration: 0.3,
                ease: "easeOut",
                type: "spring",
                stiffness: 300,
                damping: 25
              }}
            >
              ¡Escríbenos por WhatsApp!
            </ContactText>
          )}
        </WhatsAppButton>
        
        <NotificationContainer />
        <ModeSwitcher />
      </AppContainer>
                    </NotificationProvider>
                  </SecurityProvider>
                </AdminProvider>
              </EcommerceProvider>
            </ShippingProvider>
          </PaymentProvider>
        </AuthProvider>
      </Router>
  );
});

App.displayName = 'App';

export default App; 